import { Injectable } from '@angular/core';
import { MatDrawerMode } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { MenuItem } from '../models/menu.item';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { SharedService } from './shared.service';

const isOpenedStoreKey = 'is_opened_left_menu';
const leftMenuStateKey = 'left_menu_state_6';

type MenuState = Record<string, { opened?: boolean }>;

@Injectable({
  providedIn: 'root',
})
export class NavbarService {
  public readonly version = 'STAGE - 10.17.0';
  public activeLink = new BehaviorSubject('');
  public isOpenedMenu = new BehaviorSubject(
    this.localStorageService.getBoolean(isOpenedStoreKey),
  );
  public modeMenu: MatDrawerMode = 'side';
  public groups: MenuItem[] = [];
  public groupsSource: MenuItem[] = [
    // {
    //   title: 'Dashboard',
    //   opened: true,
    //   items: [
    //     {
    //       title: 'Updates',
    //       path: '/',
    //       icon: 'dashboard',
    //     },
    //   ],
    // },
    {
      title: 'Users',
      opened: true,
      items: [
        {
          title: 'Agents',
          path: '/users',
          icon: 'person_outline',
        },
        {
          title: 'Deals',
          path: '/deals',
          icon: 'description',
        },
        {
          title: 'Facilities',
          path: '/facilities',
          icon: 'account_balance',
        },
        {
          title: 'Credit Cards',
          path: '/credit-cards',
          icon: 'credit_card',
        },
        {
          title: 'Payments',
          path: '/promised-payments',
          icon: 'schedule',
        },
        {
          title: 'Payments By Deal',
          path: '/payments-overview',
          icon: 'reviews',
        },
        {
          title: 'Transactions',
          path: '/transactions',
          icon: 'attach_money',
        },
        {
          title: 'Checks',
          path: '/paper-check-requests',
          icon: 'description',
        },
        // {
        //   title: 'Default Candidates',
        //   path: '/default-candidates',
        //   icon: 'attach_money',
        // },
      ],
    },
    {
      title: 'Guarantors',
      opened: true,
      items: [
        {
          title: 'Brokers',
          path: '/brokers',
          icon: 'account_balance',
        },
        {
          title: 'Title Companies',
          path: '/commission-issuers',
          icon: 'group',
        },
        {
          title: 'CDA',
          path: '/cda',
          icon: 'picture_as_pdf',
        },
      ],
    },
    {
      title: 'Partners',
      opened: false,
      items: [
        {
          title: 'Integrations',
          path: '/integrations',
          icon: 'webhook',
        },
        {
          title: 'Partners',
          path: '/broker-users',
          icon: 'badge',
        },
        {
          title: 'Commissions',
          path: '/external-deal-commissions',
          icon: 'group_work',
        },
      ],
    },
    {
      title: 'Data',
      opened: true,
      items: [
        {
          title: 'Feature Flags',
          path: '/feature-flags',
          icon: 'bookmark',
        },
        {
          title: 'Statistics',
          path: '/stats',
          icon: 'trending_up',
        },
        {
          title: 'Reports',
          href: `${environment.reportsDomain}`,
          icon: 'bar_chart',
        },
        {
          title: 'ACH SFTP',
          path: '/ach-sftp',
          icon: 'move_to_inbox',
        },
        {
          title: 'SVB V2 ACH',
          path: '/svb-v2-ach',
          icon: 'inbox',
        },
        {
          title: 'SVB V2 Batches',
          path: '/svb-ach-batch',
          icon: 'view_timeline',
        },
        {
          title: 'TabaPay Pull',
          path: '/tabapay-pull',
          icon: 'price_check',
        },
        {
          title: 'Deals 90 Days Eligible',
          path: '/report-deals-90-days-eligible',
          icon: 'description',
        },
        {
          title: 'Logs',
          path: '/logger',
          icon: 'web_stories',
        },
      ],
    },
    {
      title: 'Settings',
      opened: false,
      items: [
        {
          title: 'Marketing Campaigns',
          path: '/marketing-campaigns',
          icon: 'calendar_today',
        },
        {
          title: 'Pricing Policies',
          path: '/pricing-policies',
          icon: 'request_quote',
        },
        {
          title: 'Pricing Policy Rules',
          path: '/pricing-policy-rules',
          icon: 'rule_settings',
        },
        {
          title: 'Manage Facilities',
          path: '/manage-facilities',
          icon: 'account_balance',
        },
        {
          title: 'Promotion Codes',
          path: '/discounts',
          icon: 'sell',
        },
        {
          title: 'Deal Promo Codes',
          path: '/deal-promo',
          icon: 'sell',
        },
        {
          title: 'Users - Reports',
          path: '/report-users',
          icon: 'assessment',
        },
        {
          title: 'Agreements',
          path: '/agreements',
          icon: 'gavel',
        },
        {
          title: 'Reset Blocked Phone Numbers',
          path: '/reset-sms-validation-cache',
          icon: 'settings',
        },
        {
          title: 'FBO to Tongo SVB Transfers',
          path: '/astra-fbo-to-tongo-svb-transfers',
          icon: 'payments',
        },
        {
          title: 'Manual Transfer',
          path: '/agent-payments',
          icon: 'send',
        },
        {
          title: 'Config',
          path: '/config',
          icon: 'settings',
        },
      ],
    },
  ];

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private sharedService: SharedService,
    private authService: AuthService,
  ) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.activeLink.next(this.router.url.split(/[?#]/)[0]);
      });

    this.isOpenedMenu.subscribe((isOpened) => {
      this.localStorageService.setBoolean(isOpenedStoreKey, isOpened);
    });

    window.addEventListener('resize', this.onResize);
    this.onResize();
    this.loadLeftMenu();

    this.authService.authUser.subscribe((user) => {
      if (!user) {
        return;
      }

      this.removeLinks();
    });
  }

  private async removeLinks(): Promise<void> {
    const groups: MenuItem[] = JSON.parse(JSON.stringify(this.groupsSource));
    const users: MenuItem = groups.find((group) => group.title === 'Users')!;

    users.items = await this.sharedService.removeLink(
      'CanViewPayments',
      'Payments By Deal',
      users.items,
      'title',
    );
    users.items = await this.sharedService.removeLink(
      'CanViewPayments',
      'Payments',
      users.items,
      'title',
    );
    users.items = await this.sharedService.removeLink(
      'HasWriteAccess',
      'Checks',
      users.items,
      'title',
    );

    const settings: MenuItem = groups.find(
      (group) => group.title === 'Settings',
    )!;

    settings.items = await this.sharedService.removeLink(
      'CanResetSmsValidationCache',
      'Reset Blocked Phone Numbers',
      settings.items,
      'title',
    );
    settings.items = await this.sharedService.removeLinkExactPermission(
      'AllowTransfersFBOToTongoSVB',
      'FBO to Tongo SVB Transfers',
      settings.items,
      'title',
    );
    settings.items = await this.sharedService.removeLinkExactPermission(
      'CanSendAgentPayments',
      'Manual Transfer',
      settings.items,
      'title',
    );

    this.groups = groups;
  }

  public openMenu(): void {
    this.isOpenedMenu.next(true);
  }

  public closeMenu(): void {
    this.isOpenedMenu.next(false);
  }

  public toggleMenu(): void {
    this.isOpenedMenu.next(!this.isOpenedMenu.value);
  }

  public async goBack() {
    if (window.history.length > 0) {
      window.history.back();
    } else {
      await this.router.navigate(['../']);
    }
  }

  private onResize = () => {
    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    const isDesktop = width > 900;
    this.modeMenu = isDesktop ? 'side' : 'over';
  };

  private loadLeftMenu(): void {
    const data =
      this.localStorageService.getAnyObject<MenuState>(leftMenuStateKey);
    if (data) {
      this.groups.forEach((group) => {
        group.opened = !!data[group.title]?.opened;
      });
    }
  }

  public updatedLeftMenu(): void {
    const data: MenuState = {};
    this.groups.forEach((group) => {
      data[group.title] = { opened: group.opened };
    });

    this.localStorageService.setAnyObject<MenuState>(leftMenuStateKey, data);
  }
}
